// dashboard/src/pages/apps-setup/GenerateTab.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ACCENT_PRESETS, OUTPUT_META, renderPhoneScreenshot, renderBanner,
  type AccentPreset, type OutputFormat,
} from '@/lib/marketing-image';

interface Shot {
  id: string;
  name: string;
  url: string;      // object URL for the decoded image
  img: HTMLImageElement;
  caption: string;
}

const BG_INDEX_KEY = 'generate-bg-index';

function renderCanvas(format: OutputFormat, img: HTMLImageElement, preset: AccentPreset, caption: string): HTMLCanvasElement {
  const opts = { preset, caption };
  return format === 'phone' ? renderPhoneScreenshot(img, opts) : renderBanner(img, opts);
}

function downloadCanvas(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) { resolve(); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so Safari reliably starts the download before the blob URL is freed.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      resolve();
    }, 'image/png');
  });
}

function safeSlug(s: string): string {
  return (s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'app').slice(0, 40);
}

function Preview({ img, format, preset, caption, filenameBase }: {
  img: HTMLImageElement; format: OutputFormat; preset: AccentPreset; caption: string; filenameBase: string;
}) {
  // Render once; the phone canvas may come back portrait or landscape depending
  // on the screenshot, so label/dimensions are read from the actual canvas.
  const canvas = useMemo(
    () => renderCanvas(format, img, preset, caption),
    [img, caption, format, preset],
  );
  const dataUrl = useMemo(() => canvas.toDataURL('image/png'), [canvas]);
  const label = format === 'banner'
    ? OUTPUT_META.banner.label
    : canvas.width > canvas.height ? 'Phone screenshot (landscape)' : OUTPUT_META.phone.label;
  const filename = `${safeSlug(filenameBase)}-${format}.png`;
  return (
    <div className="bg-surface border border-border rounded-lg p-2 space-y-2">
      <img src={dataUrl} alt={`${format} preview`} className="w-full rounded-md border border-border/50" />
      <div className="flex items-center justify-between">
        <span className="text-content-subtle text-xs">{label} · {canvas.width}×{canvas.height}</span>
        <button
          type="button"
          onClick={() => downloadCanvas(canvas, filename)}
          className="px-2 py-1 text-xs rounded-md bg-primary text-white hover:opacity-90"
        >
          Download PNG
        </button>
      </div>
    </div>
  );
}

export default function GenerateTab() {
  const [shots, setShots] = useState<Shot[]>([]);
  const [formats, setFormats] = useState<Record<OutputFormat, boolean>>({ phone: true, banner: true });
  const [bannerHeadline, setBannerHeadline] = useState('');
  const [note, setNote] = useState<string | null>(null);
  // The background auto-rotates: each generated pack uses the next of ACCENT_PRESETS,
  // persisted so the next pack (even after reload) advances on its own.
  const [bgIndex, setBgIndex] = useState(() => {
    try {
      const raw = localStorage.getItem(BG_INDEX_KEY);
      const n = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(n) && n >= 0 ? n % ACCENT_PRESETS.length : 0;
    } catch { return 0; }
  });

  const preset = ACCENT_PRESETS[bgIndex % ACCENT_PRESETS.length];
  const noFormat = !formats.phone && !formats.banner;

  useEffect(() => {
    try { localStorage.setItem(BG_INDEX_KEY, String(bgIndex)); } catch { /* ignore */ }
  }, [bgIndex]);
  function advanceBg() { setBgIndex((i) => (i + 1) % ACCENT_PRESETS.length); }

  // Revoke object URLs on unmount.
  const shotsRef = useRef<Shot[]>([]);
  useEffect(() => { shotsRef.current = shots; }, [shots]);
  useEffect(() => () => { shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url)); }, []);

  async function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    const files = Array.from(fileList);
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length < files.length) setNote('Skipped non-image files.');
    else setNote(null);
    const loaded = await Promise.all(images.map((file) => new Promise<Shot | null>((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve({ id: `${file.name}-${img.naturalWidth}x${img.naturalHeight}-${crypto.randomUUID()}`, name: file.name.replace(/\.[^.]+$/, ''), url, img, caption: '' });
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    })));
    setShots((prev) => [...prev, ...loaded.filter((s): s is Shot => s !== null)]);
  }

  function setCaption(id: string, caption: string) {
    setShots((prev) => prev.map((s) => (s.id === id ? { ...s, caption } : s)));
  }
  function removeShot(id: string) {
    const target = shots.find((s) => s.id === id);
    if (target) URL.revokeObjectURL(target.url);
    setShots((prev) => prev.filter((s) => s.id !== id));
  }

  // A "pack" = every phone screenshot + a single feature banner, all on the
  // current background. After downloading, advance to the next background so
  // the next pack is automatically different — no picking.
  async function downloadAll() {
    for (const shot of shots) {
      if (formats.phone) {
        // eslint-disable-next-line no-await-in-loop
        await downloadCanvas(renderCanvas('phone', shot.img, preset, shot.caption), `${safeSlug(shot.caption || shot.name)}-phone.png`);
      }
    }
    if (formats.banner && shots[0]) {
      await downloadCanvas(renderCanvas('banner', shots[0].img, preset, bannerHeadline), `${safeSlug(bannerHeadline || shots[0].name)}-banner.png`);
    }
    advanceBg();
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-medium text-content">Generate store images</h2>
        <p className="text-content-muted text-sm mt-1">
          Upload app screenshots to composite framed store screenshots and a feature banner. Portrait and landscape (album) shots are detected automatically per image and framed to match. No AI — your screenshot is pasted 1:1, so text stays crisp. The background rotates automatically with every pack you download. Nothing is uploaded or saved.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Formats</span>
          <div className="flex gap-3">
            {(Object.keys(OUTPUT_META) as OutputFormat[]).map((f) => (
              <label key={f} className="flex items-center gap-1.5 text-sm text-content">
                <input
                  type="checkbox"
                  checked={formats[f]}
                  onChange={(e) => setFormats((m) => ({ ...m, [f]: e.target.checked }))}
                />
                {OUTPUT_META[f].label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Background (auto)</span>
          <button
            type="button"
            onClick={advanceBg}
            title="Use the next background"
            className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-surface-muted hover:border-border-strong"
          >
            <span
              className="w-5 h-5 rounded border border-border/60 shrink-0"
              style={{ background: `linear-gradient(to bottom, ${preset.top}, ${preset.bottom})` }}
            />
            <span className="text-sm text-content">{bgIndex + 1} / {ACCENT_PRESETS.length}</span>
            <span className="text-content-subtle text-xs">Next ›</span>
          </button>
        </div>

        <label className="ml-auto shrink-0 px-3 py-2 text-sm rounded-md bg-primary text-white hover:opacity-90 cursor-pointer">
          Upload screenshots
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        </label>
      </div>
      {note && <p className="text-content-subtle text-xs">{note}</p>}

      {shots.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center text-content-subtle text-sm">
          No screenshots yet. Upload one or more to generate store images.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-content-subtle text-sm">{shots.length} screenshot{shots.length > 1 ? 's' : ''}</span>
            <button
              type="button"
              disabled={noFormat}
              onClick={downloadAll}
              className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
            >
              Download pack
            </button>
          </div>

          {noFormat && <p className="text-content-subtle text-xs">Select at least one format.</p>}

          {/* Phone screenshots — one per uploaded screenshot */}
          <div className="space-y-6">
            {shots.map((shot) => (
              <div key={shot.id} className="bg-surface border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <img src={shot.url} alt={shot.name} className="w-12 h-12 rounded-md object-cover border border-border" />
                  <input
                    placeholder="Caption (optional)"
                    value={shot.caption}
                    onChange={(e) => setCaption(shot.id, e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
                  />
                  <button type="button" onClick={() => removeShot(shot.id)} className="text-content-subtle hover:text-content text-sm shrink-0">remove</button>
                </div>
                {formats.phone && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Preview img={shot.img} format="phone" preset={preset} caption={shot.caption} filenameBase={shot.caption || shot.name} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Feature banner — a single banner for the whole pack */}
          {formats.banner && shots[0] && (
            <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-content font-medium text-sm">Feature banner</span>
                <span className="text-content-subtle text-xs">uses the first screenshot</span>
              </div>
              <input
                placeholder="Banner headline (optional)"
                value={bannerHeadline}
                onChange={(e) => setBannerHeadline(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
              />
              <div className="max-w-2xl">
                <Preview img={shots[0].img} format="banner" preset={preset} caption={bannerHeadline} filenameBase={bannerHeadline || shots[0].name} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
